// Settings pages (prototype 70-settings.js): sections, the sticky save bar, read-only notes,
// and the draft that asks before leaving with unsaved changes.
import { type ReactNode, useEffect } from 'react';
import { L } from '../lib/prefs';
import { setLeaveGuard } from '../lib/route';
import { app, can } from '../lib/session';
import { Btn, Ic, Note } from './kit';
import { confirmBox } from './modal';

export function SetSection({ id, icon, title, sub, children }: { id: string; icon: string; title: ReactNode; sub?: ReactNode; children: ReactNode }) {
  return (
    <section className="set-sec" id={'sec-' + id}>
      <header><span className="ss-ic"><Ic n={icon} /></span><div><h2>{title}</h2>{sub ? <p>{sub}</p> : null}</div></header>
      <div className="set-body">{children}</div>
    </section>
  );
}

export function SaveBar({ onDiscard, onSave, disabled, why, busy }: { onDiscard: () => void; onSave: () => void; disabled?: boolean; why?: string; busy?: boolean }) {
  return (
    <div className="savebar">
      <span><Ic n="info" />{L('You have unsaved changes', 'لديك تغييرات غير محفوظة')}</span>
      <Btn kind="ghost" onClick={onDiscard}>{L('Discard', 'تجاهل')}</Btn>
      <Btn kind="primary" onClick={onSave} disabled={disabled} why={why} busy={busy}>{L('Save changes', 'حفظ التغييرات')}</Btn>
    </div>
  );
}

/** The prototype's roNote(): auditors read everything; a locked company is read-only for all. */
export function RoNote() {
  const { me, co } = app.get();
  if (me?.role === 'auditor') return <Note icon="eye">{L('You have read-only access as an auditor. You can look at everything and export, but not change anything.', 'لديك صلاحية قراءة فقط كمدقق. يمكنك الاطلاع والتصدير دون تعديل.')}</Note>;
  if (co?.status === 'locked') return <Note tone="bad" icon="lock">{L('This company is locked until the overdue invoice is paid, so everything is read-only.', 'هذه الشركة مقفلة حتى دفع الفاتورة المتأخرة، لذا كل شيء للقراءة فقط.')}</Note>;
  return null;
}
/** Settings are changed by owners and admins; everyone else who can open them reads. */
export const roSet = () => !can('admin');

/** While `dirty`, leaving the page asks "Leave without saving?" first. */
export function useLeaveGuard(dirty: boolean, discard: () => void) {
  useEffect(() => {
    if (!dirty) { setLeaveGuard(null); return; }
    setLeaveGuard(proceed => {
      confirmBox({
        title: L('Leave without saving?', 'المغادرة دون حفظ؟'),
        body: L('You have changes on this page that are not saved.', 'لديك تغييرات غير محفوظة في هذه الصفحة.'),
        ok: L('Discard changes', 'تجاهل التغييرات'), danger: true,
        onOk: () => { discard(); proceed(); },
      });
      return true;
    });
    const before = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', before);
    return () => { setLeaveGuard(null); window.removeEventListener('beforeunload', before); };
  }, [dirty, discard]);
}

export function SRow({ label, help, children, stack, cls }: { label: ReactNode; help?: ReactNode; children: ReactNode; stack?: boolean; cls?: string }) {
  return (
    <div className={['set-row', cls, stack && 'stack'].filter(Boolean).join(' ')}>
      <div className="sr-t"><b>{label}</b>{help ? <small>{help}</small> : null}</div>
      <div className="sr-c">{children}</div>
    </div>
  );
}

export function SetNav({ items }: { items: [string, string][] }) {
  return (
    <nav className="set-nav" aria-label={L('Sections', 'الأقسام')}>
      {items.map(([id, label]) => (
        <a key={id} href={location.hash || '#'} onClick={e => {
          e.preventDefault();
          const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
          document.getElementById('sec-' + id)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }}>{label}</a>
      ))}
    </nav>
  );
}
